"use client";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  ArrowLeft02Icon,
  ArrowRight02Icon,
  Calendar03Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import * as React from "react";
import { type DateRange, type Matcher } from "react-day-picker";
import { getDateLib } from "react-day-picker/persian";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import formatPerisanDate from "@/utils/formatPerisanDate";
import { Typography } from "../ui/typography";
import { ResponsivePopover } from "../ui/responsive-popover";
import { getPdpCalendarWindow } from "@/utils/pdp/calendar-window";
import { isPdpCalendarDayDisabled } from "@/utils/pdp/isPdpCalendarDayDisabled";

interface DateRangePickerModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dateRange?: DateRange;
  onSelect?: (range: DateRange | undefined) => void;
  disabled?: boolean;
  onSelectionComplete?: () => void;
  /** YYYY-MM-DD allowlist from the PDP calendar endpoint. Omit on search. */
  availableDates?: string[];
  /** Fail-closed while the allowlist is loading and no data is in cache. */
  isAvailabilityPending?: boolean;
  /** PDP-only: dates missing from `availableDates` are not selectable. */
  constrainAvailability?: boolean;
}

export default function DateRangePickerModal({
  open,
  setOpen,
  dateRange,
  onSelect,
  disabled = false,
  onSelectionComplete,
  availableDates,
  isAvailabilityPending = false,
  constrainAvailability = false,
}: DateRangePickerModalProps) {
  const dateLib = getDateLib();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [localSelectedRange, setLocalSelectedRange] = React.useState<
    DateRange | undefined
  >(dateRange);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const selectedRange = React.useMemo(() => {
    return localSelectedRange ?? dateRange;
  }, [localSelectedRange, dateRange]);

  const today = React.useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const usesAvailability =
    constrainAvailability ||
    isAvailabilityPending ||
    availableDates !== undefined;

  // Search: today through two Jalali months. PDP: same window, capped at 62 days.
  const maxDate = React.useMemo(
    () =>
      usesAvailability
        ? getPdpCalendarWindow(today).to
        : getDateLib().addMonths(today, 2),
    [today, usesAvailability],
  );

  const availableSet = React.useMemo(
    () =>
      availableDates === undefined ? undefined : new Set(availableDates),
    [availableDates],
  );

  const isDayUnavailable = React.useCallback(
    (date: Date) =>
      isPdpCalendarDayDisabled(date, {
        availableDates: availableSet,
        isAvailabilityPending,
        selectedRange,
        today,
        maxDate,
      }),
    [availableSet, isAvailabilityPending, selectedRange, today, maxDate],
  );

  const disabledDays = React.useMemo<Matcher | Matcher[] | boolean>(() => {
    if (disabled) return true;

    const bounds: Matcher[] = [{ before: today }, { after: maxDate }];
    if (!usesAvailability) return bounds;

    return [...bounds, isDayUnavailable];
  }, [disabled, today, maxDate, usesAvailability, isDayUnavailable]);

  // Ensure space for popover before opening
  React.useEffect(() => {
    if (open && !disabled && !isMobile && triggerRef.current) {
      const buttonRect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Estimate popover height
      const popoverHeight = 500;
      const additionalPadding = 50;
      const requiredSpace = popoverHeight + additionalPadding;

      // Calculate available space below
      const spaceBelow = viewportHeight - buttonRect.bottom;

      // If not enough space, scroll down
      if (spaceBelow < requiredSpace) {
        const scrollAmount = requiredSpace - spaceBelow;
        window.scrollBy({
          top: scrollAmount,
          behavior: "smooth",
        });
      }
    }
  }, [open, disabled, isMobile]);

  const handleConfirm = () => {
    if (disabled) return;
    if (usesAvailability && selectedRange?.from && selectedRange?.to) {
      const checkoutBlocked = isPdpCalendarDayDisabled(selectedRange.to, {
        availableDates: availableSet,
        isAvailabilityPending,
        selectedRange: { from: selectedRange.from, to: undefined },
        today,
        maxDate,
      });
      if (checkoutBlocked) return;
    }
    setOpen(false);
    onSelect?.(selectedRange);
    onSelectionComplete?.();
  };

  const handleSelect = (
    range: DateRange | undefined,
    triggerDate?: Date,
  ) => {
    if (disabled) return;

    const clicked = triggerDate ?? range?.to ?? range?.from;
    if (usesAvailability && clicked && isDayUnavailable(clicked)) return;

    // Complete range: any new click resets and starts from the clicked date
    if (selectedRange?.from && selectedRange?.to) {
      const newRange = { from: triggerDate ?? range?.from, to: undefined };
      setLocalSelectedRange(newRange);
      return;
    }

    if (range?.from === range?.to) {
      const newRange = { from: range?.from, to: undefined };
      setLocalSelectedRange(newRange);
      return;
    }

    if (
      !selectedRange?.to &&
      selectedRange?.from &&
      range?.to &&
      dateLib.format(selectedRange?.from, "yyyy/MM/dd") ===
        dateLib.format(range?.to, "yyyy/MM/dd")
    ) {
      const newRange = { from: range?.from, to: undefined };
      setLocalSelectedRange(newRange);
      return;
    }

    if (range?.from && range?.to && range?.from > range?.to) {
      const newRange = { from: range?.from, to: undefined };
      setLocalSelectedRange(newRange);
      return;
    }

    setLocalSelectedRange(range);

    if (range?.from && range?.to && !isMobile) {
      onSelect?.(range);
      setOpen(false);
      setTimeout(() => {
        onSelectionComplete?.();
      }, 100);
    }
  };

  const mobileFooter = (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between text-center">
        <div className="flex-1">
          <Typography
            variant="captionRegular"
            color="black300"
            className="mb-2"
          >
            تاریخ ورود
          </Typography>
          <Typography variant="bodyMMedium" className="text-[13px]">
            {selectedRange?.from
              ? formatPerisanDate(selectedRange.from, "EEEE، dd MMMM")
              : null}
          </Typography>
        </div>
        <div className="flex items-center justify-center px-2">
          <HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={1.5} size={24} />
        </div>
        <div className="flex-1">
          <Typography
            variant="captionRegular"
            color="black300"
            className="mb-2"
          >
            تاریخ خروج
          </Typography>
          <Typography variant="bodyMMedium" className="text-[13px]">
            {selectedRange?.to
              ? formatPerisanDate(selectedRange.to, "EEEE، dd MMMM")
              : null}
          </Typography>
        </div>
      </div>
      <Button
        size={"lg"}
        onClick={handleConfirm}
        disabled={!selectedRange?.from || disabled}
      >
        تایید تاریخ
      </Button>
    </div>
  );

  return (
    <ResponsivePopover
      open={open && !disabled}
      onOpenChange={(isOpen) => !disabled && setOpen(isOpen)}
      className="w-full!"
      disabled={disabled}
      trigger={
        <Button
          ref={triggerRef}
          id="date"
          variant={"ghost"}
          className={cn(
            "px-4 py-2.5 h-auto justify-start text-foreground text-xs md:text-[13px] font-normal md:text-sm hover:bg-transparent! hover:text-foreground cursor-pointer disabled:opacity-100",
            !selectedRange && "text-muted-foreground",
          )}
          disabled={disabled}
        >
          <HugeiconsIcon
            icon={Calendar03Icon}
            size={20}
            strokeWidth={1.5}
            className="size-5"
          />
          {selectedRange?.from ? (
            selectedRange.to ? (
              <>
                {formatPerisanDate(selectedRange.from)} -{" "}
                {formatPerisanDate(selectedRange.to)}
              </>
            ) : (
              formatPerisanDate(selectedRange.from)
            )
          ) : (
            <span>تاریخ سفر</span>
          )}
        </Button>
      }
      footer={isMobile ? mobileFooter : null}
    >
      <Button
        variant={"ghost"}
        onClick={handleConfirm}
        disabled={!selectedRange || disabled}
        className="self-start gap-3 md:hidden"
      >
        <HugeiconsIcon
          icon={ArrowRight02Icon}
          size={20}
          className="size-5"
          strokeWidth={1.5}
        />
        تاریخ سفر
      </Button>
      <Calendar
        key={
          usesAvailability
            ? `pdp-${availableDates?.join(",") ?? "pending"}`
            : "search"
        }
        autoFocus={!disabled}
        mode="range"
        defaultMonth={selectedRange?.from}
        selected={selectedRange}
        onSelect={handleSelect}
        numberOfMonths={2}
        endMonth={maxDate}
        disabled={disabledDays}
        excludeDisabled={usesAvailability}
        showYearSwitcher={false}
      />
    </ResponsivePopover>
  );
}
